import { DestroyRef, inject, Inject, Injectable } from '@angular/core';
import { ConfigInstanceToken, ConfigInterface } from '../../interfaces/config.interface';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import moment from 'moment';
import { combineLatest, Observable, throwError } from 'rxjs';
import { filter, map, mergeMap, take, tap } from 'rxjs/operators';
import { TimesheetAndExpensesInterface } from './timesheet-and-expenses.interface';
import { AddressModel } from '../../models/complex/address.model';
import { TimesheetAndExpensesP58Model } from '../../models/complex/timesheet-and-expenses-p58.model';
import { TimesheetNotificationModelFromJson } from '../../models/low-level/timesheet-notification.model';
import { TimesheetModel, TimesheetModelFromJson } from '../../models/low-level/timesheet.model';
import { EventService } from '../event/event.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TIMESHEET_KEYS } from '../../enums/timesheet.enum';
import { isDefined } from '../../utilities/is-defined';
import { NotificationStatusInstanceToken } from '../../interfaces/notification-status.interface';
import { Action, Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import {
    initTimesheet,
    saveTimesheetCollectionRequested,
    submitTimesheetRequested,
    timesheetCollectionSaved,
    timesheetCollectionStorageSyncError,
    timesheetSubmitFailed,
    timesheetSubmitPersisted,
    timesheetSubmitSynced,
    updateStartOfWork,
} from '../../state/timesheet/actions/timesheet.actions';
import { createTimesheetState, TimesheetState } from '../../state/timesheet/timesheet-collection.state';
import { toJsonTimesheetModel } from '../../state/timesheet/utils/to-json-timesheet-model';
import { getUuidV4 } from '../../utilities/generic-utilities';
import {
    selectCurrentStartOfWork,
    selectCurrentTimesheet,
    selectSubmittedTimesheet,
} from '../../state/timesheet/selectors/timesheet.selector';
import {
    addAddressesRequested,
    addressesSaved,
    addressesUnchanged,
    addressStorageSyncError,
    deleteAddressRequested,
    hideAddressRequested,
    initAddresses,
    resetAddressVisibility,
    saveAddressesRequested,
} from '../../state/address/actions/address.actions';
import { selectAddresses, selectAddressStatus } from '../../state/address/selectors/address.selector';
import { AddressState, fromAddressState, toAddressState } from '../../state/address';
import { selectUserCountry } from '../../state/user-master/selectors/user-master.selector';

/**
 * Bridges a timesheet slice into the legacy `TimesheetModelFromJson` the confirmation, header,
 * side-menu and travel-status-update code paths still read. Resolving a stored address id back
 * into the legacy JSON address shape needs the address collection and the user's country, which is
 * why they ride alongside the timesheet rather than being looked up inside `toJsonTimesheetModel`
 * itself — that keeps the pure transform free of a store dependency.
 *
 * @param t the timesheet slice to convert
 * @param key which timesheet this is, for the legacy model's own bookkeeping
 * @param addresses the address collection a stored address id is resolved against
 * @param userCountry the country every timesheet address carries, from user master
 * @returns the legacy timesheet model
 */
export function convertTimesheetStateToTimesheetModel(
    t: TimesheetState,
    key: TIMESHEET_KEYS,
    addresses: readonly AddressState[],
    userCountry: string,
): TimesheetModelFromJson {
    return new TimesheetModelFromJson(toJsonTimesheetModel(t, addresses, userCountry), key);
}

@Injectable({
    providedIn: 'root',
})
export class TimesheetAndExpensesNgrxService implements TimesheetAndExpensesInterface {
    public isInitialized = false;

    private readonly notificationStatusService = inject(NotificationStatusInstanceToken);
    private currentTimesheet$: Observable<TimesheetModel>;
    private submittedTimesheet$: Observable<TimesheetModel>;

    /** Monotonic counter used to correlate a save request with its outcome action. */
    private saveCorrelationCounter = 0;

    public constructor(
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly eventService: EventService,
        private readonly store: Store,
        private readonly actions$: Actions,
        private readonly destroyRef: DestroyRef,
    ) {
        // Reads come from the timesheet store, which is the source of truth; TimesheetEffects
        // rehydrates it from the concurrency store on init and on sync-completion. Writes still
        // flow through this facade (see save*/submit*), which keeps the model transforms here.
        this.currentTimesheet$ = combineLatest([
            this.store.select(selectCurrentTimesheet).pipe(filter(isDefined)),
            this.store.select(selectAddresses),
            this.store.select(selectUserCountry),
        ]).pipe(
            map(([t, addresses, userCountry]) =>
                convertTimesheetStateToTimesheetModel(t, TIMESHEET_KEYS.CURRENT_TIMESHEET, addresses, userCountry),
            ),
        );
        this.submittedTimesheet$ = combineLatest([
            this.store.select(selectSubmittedTimesheet).pipe(
                // the legacy code consuming this service expects that a submitted timesheet exists; so if the state does not have one,
                // we create an empty invalid one as fallback.
                // Use-case is typically to compare the end-of-work of the submitted timesheet with the current timesheet's start-of-work
                // to be able to issue the "11 hour" break warning. This comparison also works with an invalid end-of-work.
                map((ts) => (isDefined(ts) ? ts : { ...createTimesheetState(getUuidV4()), isSubmitted: true })),
            ),
            this.store.select(selectAddresses),
            this.store.select(selectUserCountry),
        ]).pipe(
            map(([t, addresses, userCountry]) =>
                convertTimesheetStateToTimesheetModel(t, TIMESHEET_KEYS.SUBMITTED_TIMESHEET, addresses, userCountry),
            ),
        );

        this.eventService.synchronizationRequested$
            .pipe(
                filter(() => this.isInitialized),
                mergeMap(() => combineLatest([this.currentTimesheet$, this.submittedTimesheet$]).pipe(take(1))),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    public init(): Observable<void> {
        this.log.debug(`TimesheetAndExpensesService: init: init the timesheet and expenses service (${Date.now()})`);
        // Rehydrate the timesheet slice from storage before the seeding writes below land.
        this.store.dispatch(initTimesheet());

        // this init should happen when the addresses are being extracted from the notification service
        this.store.dispatch(initAddresses());

        return this.store.select(selectAddressStatus).pipe(
            filter((addressStatus) => addressStatus === 'ready'),
            tap(() => {
                this.isInitialized = true;
            }),
            map(() => undefined),
        );
    }

    public selectCurrentTimesheet$(options?: { once: true }): Observable<TimesheetModel> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: selectCurrentTimesheet$: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }

        return options?.once ? this.currentTimesheet$.pipe(take(1)) : this.currentTimesheet$;
    }

    public saveCurrentTimesheet$(timesheet: TimesheetModel): Observable<TimesheetModel> {
        this.log.debug(
            `TimesheetAndExpensesService: saveCurrentTimesheet: save a timesheet to UserProfileSet and IndexedDb. (${Date.now()})`,
        );
        return this.dispatchTimesheetSave().pipe(map(() => timesheet));
    }

    /**
     * Requests persistence of the given timesheet through the timesheet store's
     * save effect. The slice already holds the value (the store is the source of
     * truth); this bridge only preserves the former write contract for callers.
     *
     * @returns an observable emitting the stored shape (with the `saveTimestamp`
     * stamped by storage) on success and erroring on failure
     */
    private dispatchTimesheetSave(): Observable<ReturnType<typeof timesheetCollectionSaved>> {
        const correlationId = `timesheet-save-${++this.saveCorrelationCounter}`;
        return this.dispatchSaveRequest(
            saveTimesheetCollectionRequested({ correlationId }),
            this.actions$.pipe(ofType(timesheetCollectionSaved, timesheetCollectionStorageSyncError)),
            correlationId,
            timesheetCollectionStorageSyncError.type,
        ).pipe(
            // dispatchSaveRequest errors on the failure outcome, so only the
            // success action reaches this projection.
            map((outcome) => outcome as ReturnType<typeof timesheetCollectionSaved>),
        );
    }

    /**
     * Not supported behind the store.
     *
     * This is the legacy `TimesheetAndExpensesComponent`'s write path, and that component is
     * only routed while `enableNgRx` is off — which is every configuration
     * except* the one where this NgRx facade is provided at all. The path is therefore
     * unreachable, and the snapshot-integration machinery it needed has been removed rather
     * than carried along; the store-connected component commits its form directly.
     *
     * It fails loudly instead of no-opping: this is a save, so silently dropping the user's
     * edits would be the worse outcome if the reachability assumption above were ever wrong.
     *
     * @returns an observable that always errors
     */
    public saveTimesheetFormModel(): Observable<TimesheetModel | void> {
        return throwError(
            () =>
                new Error(
                    'TimesheetAndExpensesNgrxService: saveTimesheetFormModel: the legacy form-model write path is not supported behind the store',
                ),
        );
    }

    /**
     * Dispatches a current-timesheet write action (form integration, start of
     * work). The reducer applies the update to the slice synchronously; the
     * persist effect then requests a storage save of the updated slice under
     * the same correlation id, whose outcome settles the returned observable.
     *
     * @param request the write action to dispatch, carrying the correlation id
     * @param correlationId the id carried by the request and echoed by the outcome
     * @returns an observable emitting the persisted current timesheet
     */
    private dispatchCurrentTimesheetUpdate(request: Action, correlationId: string): Observable<TimesheetModel> {
        return this.dispatchSaveRequest(
            request,
            this.actions$.pipe(ofType(timesheetCollectionSaved, timesheetCollectionStorageSyncError)),
            correlationId,
            timesheetCollectionStorageSyncError.type,
        ).pipe(
            // dispatchSaveRequest errors on the failure outcome, so only the
            // success action reaches this projection.
            mergeMap(() =>
                combineLatest([
                    this.store.select(selectCurrentTimesheet),
                    this.store.select(selectAddresses),
                    this.store.select(selectUserCountry),
                ]).pipe(
                    take(1),
                    map(([t, addresses, userCountry]) => {
                        if (!isDefined(t)) {
                            throw new Error(
                                `TimesheetAndExpensesNgrxService: dispatchCurrentTimesheetUpdate: no current timesheet defined!`,
                            );
                        }
                        return convertTimesheetStateToTimesheetModel(
                            t,
                            TIMESHEET_KEYS.CURRENT_TIMESHEET,
                            addresses,
                            userCountry,
                        );
                    }),
                ),
            ),
        );
    }

    public submit(model: TimesheetAndExpensesP58Model): Observable<void> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: submit: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }

        const correlationId = `timesheet-submit-${++this.saveCorrelationCounter}`;
        // The out model and the empty replacement timesheet are now built by the submit effect
        // off the serializable current-timesheet slice; the facade only bridges the outcome
        // back to the caller and flips the form model to read-only once persisted.

        return new Observable<void>((subscriber) => {
            // Mark the timesheet and expenses P58 model as read only once the
            // swapped timesheets are persisted, to prevent writing back to the
            // timesheet on indexeddb — regardless of the offline sync outcome.
            const readonlySubscription = this.actions$
                .pipe(
                    ofType(timesheetSubmitPersisted),
                    filter((action) => action.correlationId === correlationId),
                    take(1),
                )
                .subscribe(() => {
                    model.readonly = true;
                });
            // The submit settles once the offline queue flush completed, keeping
            // the former contract: the component navigates away (and maps errors
            // to notifications) based on this observable.
            const outcomeSubscription = this.actions$
                .pipe(
                    ofType(timesheetSubmitSynced, timesheetSubmitFailed),
                    filter((action) => action.correlationId === correlationId),
                    take(1),
                )
                .subscribe((action) => {
                    if (action.type === timesheetSubmitFailed.type) {
                        // re-throw the raw error string: the component matches
                        // submit errors against error-code enums
                        subscriber.error(action.error);
                        return;
                    }
                    subscriber.next(undefined);
                    subscriber.complete();
                });
            this.store.dispatch(submitTimesheetRequested({ correlationId }));
            return () => {
                readonlySubscription.unsubscribe();
                outcomeSubscription.unsubscribe();
            };
        });
    }

    public selectSubmittedTimesheet$(): Observable<TimesheetModel> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: selectSubmittedTimesheet: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }
        return this.submittedTimesheet$;
    }

    public getStartOfWork$(): Observable<moment.Moment> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: getStartOfWork: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }
        // undefined only while the slice holds no current timesheet — the
        // filter mirrors the former read through selectCurrentTimesheet$
        return this.store.select(selectCurrentStartOfWork).pipe(filter(isDefined));
    }

    public saveStartOfWork(newStartOfWork: moment.Moment): Observable<TimesheetModel> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: saveStartOfWork: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }
        // No unchanged-value guard needed: saveDistinctTimesheet$ skips
        // identical writes when the persist effect hands the slice to storage.
        const correlationId = `timesheet-start-of-work-${++this.saveCorrelationCounter}`;
        return this.dispatchCurrentTimesheetUpdate(
            updateStartOfWork({ startOfWork: newStartOfWork.toJSON(), correlationId }),
            correlationId,
        );
    }

    public deleteSubmittedNotificationInfoFromTimesheet(
        timesheet: Readonly<TimesheetModel>,
    ): Observable<TimesheetModel> {
        const notifications = timesheet.notifications.filter(
            (notification: TimesheetNotificationModelFromJson) => !notification.isConfirmationSubmitted,
        );
        return this.saveCurrentTimesheet$({ ...timesheet, notifications });
    }

    /**
     * Unreachable on this implementation: a confirmation's timesheet entries are now committed by the
     * timesheet slice reducing `submitConfirmationRequested`, so nothing asks the facade to write them.
     *
     * The member stays only because {@link TimesheetAndExpensesInterface} still declares it for the
     * legacy implementation, which the very same `enableNgRx` flag selects — so a legacy confirmation
     * service is never paired with this one. It throws rather than quietly doing nothing, so that
     * splitting those flags apart fails loudly instead of silently dropping a technician's times.
     *
     * @returns never; always throws
     */
    public saveTimesheetNotification(): Observable<TimesheetModel> {
        throw new Error(
            `TimesheetAndExpensesNgrxService: saveTimesheetNotification: confirmation entries are committed by the timesheet slice, so this path is unused on the NgRx implementation`,
        );
    }

    /**
     * Synchronous read of the rehydrated address collection. NgRx selects emit
     * synchronously, and the memoized selector returns the same model instances
     * between writes — mirroring the shared `private addresses` array this facade
     * used to hold.
     *
     * @returns the current address models from the store
     * @throws Error if the service is not initialized
     */
    public getAddresses(): AddressModel[] {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: getAddresses: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }

        return fromAddressState(this.store.selectSignal(selectAddresses)());
    }

    public saveAddresses(addresses: AddressModel[]): Observable<void> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: saveAddresses: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }
        // The config-gated USA-state transform stays here (it needs the model
        // instances and injected services); the store/effect only see the
        // resulting serializable collection.
        // TODO: Once this facade gets dissolved, then the code extracting the addresses from the notification and DOPs
        //       needs to ensure that the "city;state" split happens before calling the saveAddressesRequested action
        if (this.configService.getEnableStatesOfUSADropdown()) {
            addresses.forEach((address) => AddressModel.updateStateOfUSA(address));
        }
        return this.dispatchAddressSave(toAddressState(addresses));
    }

    /**
     * Requests persistence of the given collection through the address store's
     * save effect; the returned observable completes once the collection has
     * been persisted (and the slice updated) and errors if persistence fails.
     *
     * @param addresses the serializable collection to persist
     * @returns an observable that completes on success and errors on failure
     */
    private dispatchAddressSave(addresses: AddressState[]): Observable<void> {
        const correlationId = `address-save-${++this.saveCorrelationCounter}`;
        return this.dispatchSaveRequest(
            saveAddressesRequested({ addresses, correlationId }),
            this.actions$.pipe(ofType(addressesSaved, addressStorageSyncError)),
            correlationId,
            addressStorageSyncError.type,
        ).pipe(map(() => undefined));
    }

    /**
     * Dispatches a save request to a slice's save effect and mirrors the former
     * imperative write contract: the returned observable emits the matching
     * success outcome action once the payload has been persisted and errors if
     * persistence fails.
     *
     * The outcome subscription is set up before dispatching so a synchronous
     * effect result cannot be missed, and the correlation id keeps this call
     * from settling on a concurrent write's result.
     *
     * @param request the save-request action to dispatch
     * @param outcome$ the slice's success/error outcome actions
     * @param correlationId the id carried by the request and echoed by the outcome
     * @param errorType the action type marking the failure outcome
     * @returns an observable emitting the success action, or erroring on failure
     */
    private dispatchSaveRequest<TOutcome extends Action & { correlationId?: string }>(
        request: Action,
        outcome$: Observable<TOutcome>,
        correlationId: string,
        errorType: string,
    ): Observable<TOutcome> {
        return new Observable<TOutcome>((subscriber) => {
            const subscription = outcome$
                .pipe(
                    filter((action) => action.correlationId === correlationId),
                    take(1),
                )
                .subscribe((action) => {
                    if (action.type === errorType) {
                        subscriber.error(new Error((action as TOutcome & { error: string }).error));
                        return;
                    }
                    subscriber.next(action);
                    subscriber.complete();
                });
            this.store.dispatch(request);
            return () => subscription.unsubscribe();
        });
    }

    public addAddressesAndSave(newAddresses: AddressModel[]): Observable<void> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: addAddress: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }
        // The add/update merge lives in the address store's merge effect; this
        // bridge settles on the persisted or unchanged outcome.
        const correlationId = `address-add-${++this.saveCorrelationCounter}`;
        return this.dispatchSaveRequest(
            addAddressesRequested({ addresses: toAddressState(newAddresses), correlationId }),
            this.actions$.pipe(ofType(addressesSaved, addressesUnchanged, addressStorageSyncError)),
            correlationId,
            addressStorageSyncError.type,
        ).pipe(map(() => undefined));
    }

    public resetAddresses(): Observable<void> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: resetAddresses: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }
        const correlationId = `address-reset-${++this.saveCorrelationCounter}`;
        return this.dispatchSaveRequest(
            resetAddressVisibility({ correlationId }),
            this.actions$.pipe(ofType(addressesSaved, addressStorageSyncError)),
            correlationId,
            addressStorageSyncError.type,
        ).pipe(map(() => undefined));
    }

    public hideAddress(address: AddressModel): Observable<void> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: hideAddress: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }
        // The model's id is authoritative: store-derived instances
        // carry the slice's id, and newly created candidates keep theirs when
        // the merge adds them. An unknown id is a no-op in the reducer.
        const correlationId = `address-hide-${++this.saveCorrelationCounter}`;
        return this.dispatchSaveRequest(
            hideAddressRequested({ id: address.id, correlationId }),
            this.actions$.pipe(ofType(addressesSaved, addressStorageSyncError)),
            correlationId,
            addressStorageSyncError.type,
        ).pipe(map(() => undefined));
    }

    /**
     * Removes an address from the local storage
     *
     * @param address the address to remove
     * @returns an observable that completes when the address is removed
     * @throws Error if the service is not initialized
     */
    public deleteAddress(address: AddressModel): Observable<void> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: deleteAddress: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }
        // The model's id is authoritative (see hideAddress); the merge
        // effect guarantees no field-equal duplicates, so removing the one
        // matching address equals the former equalsTo filter.
        const correlationId = `address-delete-${++this.saveCorrelationCounter}`;
        return this.dispatchSaveRequest(
            deleteAddressRequested({ id: address.id, correlationId }),
            this.actions$.pipe(ofType(addressesSaved, addressStorageSyncError)),
            correlationId,
            addressStorageSyncError.type,
        ).pipe(map(() => undefined));
    }
}
